/**
 * 配信コンテンツのブロック JSON（`blocks-v1`）と差し込み変数 DSL。
 *
 * `docs/CMS_DESIGN.md` の D7 / D9 / D10 / D16 / D17 / D18 が唯一の正。
 * このモジュールは**純粋関数と zod スキーマだけ**で構成し、S3 も DB も参照しない。
 * CP（保存・ビルド・プレビュー）とノード（`mailsv-bulkinject` の差し込み）が
 * **同一実装**を使うことで、「validate は通ったのにノードで違う結果が出た」を防ぐ（D45）。
 *
 * 設計上の核心が 3 つある。
 *
 * 1. **差し込み文法は閉じている。** `{{ path }}` と `{{ path | default:"…" }}` の 2 形しかない。
 *    条件分岐もループも関数呼び出しも無い。テンプレート言語を持ち込まないので、
 *    サンドボックス脱出とテンプレートインジェクションの評価対象が**存在しない**（D10）。
 * 2. **差し込み値は必ずエスケープ関数を通る。** `renderMergeTemplate()` は `escape` を
 *    必須引数にしてあり、呼び出し側が文脈（HTML 本文 / 属性 / プレーンテキスト）を
 *    選ばずに済ませることができない。`{{contact.company}}` に `<script>` が入る経路を塞ぐ。
 * 3. **法定フッタは構造で持ち、実体で検査する。** zod は「末尾に `legal_footer` が
 *    ちょうど 1 個」を要求し（D17 ②）、`checkLegalFooterMaterialized()` は
 *    展開後の文字列に法定 4 項目が実在するかを見る（D17 ③）。②は迂回できるが③は迂回できない。
 */
import { z } from 'zod';
// 公開接尾辞リスト（PSL）を同梱する実在パッケージ。apps/worker が既に使っており、
// 「同じ丸め方を CP とレンダラで共有する」ためにここへ引き上げた。
import { getDomain } from 'tldts';

/* ============================================================ HTML エスケープ */

const HTML_ESCAPE_MAP: Readonly<Record<string, string>> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
  '`': '&#96;',
};

/**
 * HTML のテキストノード・属性値の双方で安全なエスケープ。
 *
 * 属性用に別関数を作らないのは、`"` と `'` の両方を実体参照にしているため
 * 「テキスト用を属性に使ってしまった」という取り違えが起こりえないから。
 * バッククォートも落とすのは、古い IE が未クォート属性の区切りとして扱う挙動への保険。
 */
export function escapeHtml(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).replace(/[&<>"'`]/g, (ch) => HTML_ESCAPE_MAP[ch] ?? ch);
}

/** `escapeHtml` の別名。属性値であることを呼び出し側で明示したいとき用。 */
export const escapeHtmlAttribute = escapeHtml;

/** URL として通してよいスキーム。`javascript:` / `data:` / `vbscript:` を構造的に排除する。 */
export const ALLOWED_URL_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:'] as const;

/**
 * URL 文字列が安全か判定する。
 *
 * 相対 URL は**許可しない**。メール本文には base URL が無く、受信者の MUA ごとに
 * 解決結果が変わる（＝リンク先が不定になる）ため。
 */
export function isSafeUrl(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0) return false;
  // 制御文字や空白を挟んで javascript: を偽装する手口を先に潰す。
  for (const ch of trimmed) {
    const code = ch.charCodeAt(0);
    if (code <= 0x20 || code === 0x7f) return false;
  }
  try {
    const url = new URL(trimmed);
    return (ALLOWED_URL_SCHEMES as readonly string[]).includes(url.protocol);
  } catch {
    return false;
  }
}

/**
 * 差し込み値を URL の位置（`href` など）へ入れる直前に通す。
 * 許可スキーム以外は**空文字に潰す**（リンクが消えるほうが、任意スキームが通るより安全）。
 */
export function safeUrlValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  const raw = String(value);
  return isSafeUrl(raw) ? escapeHtml(raw) : '';
}

/** クエリパラメータの位置へ差し込むとき用。パーセントエンコード後に HTML エスケープする。 */
export function escapeUrlComponent(value: unknown): string {
  if (value === null || value === undefined) return '';
  return escapeHtml(encodeURIComponent(String(value)));
}

/* ======================================================= 差し込み変数 DSL */

/**
 * 差し込みキーのパス。`a.b.c` まで（3 段）。
 * 3 段までにしてあるのは `contact.attributes.<リストの属性キー>` を表現するのに必要な深さがそれだからで、
 * 際限なく深くすると「どこまでが宣言済みキーか」の静的検査ができなくなる。
 */
export const MERGE_PATH_MAX_SEGMENTS = 3;

const MERGE_PATH_SOURCE = '[A-Za-z_][A-Za-z0-9_]*(?:\\.[A-Za-z_][A-Za-z0-9_]*){0,2}';

/** 単体のパス検証用（`{{ }}` を含まない）。 */
export const MERGE_PATH_RE = new RegExp(`^${MERGE_PATH_SOURCE}$`);

/**
 * 差し込みトークン 1 個の正規表現。**これが文法の全体である。**
 *
 *   {{ contact.name }}
 *   {{ contact.name | default:"お客様" }}
 *   {{ contact.name | default:'お客様' }}
 *   {{ contact.name | default:お客様 }}
 *
 * `default:` のフィルタは 1 個だけ。パイプを 2 個続けることも、
 * `default` 以外のフィルタ名も文法上表現できない。
 */
const MERGE_TOKEN_SOURCE =
  `\\{\\{\\s*(${MERGE_PATH_SOURCE})\\s*` +
  `(?:\\|\\s*default\\s*:\\s*(?:"((?:[^"\\\\]|\\\\.)*)"|'((?:[^'\\\\]|\\\\.)*)'|([^|{}"'\\s][^|{}"']*?))\\s*)?` +
  `\\}\\}`;

const MERGE_TOKEN_RE = new RegExp(MERGE_TOKEN_SOURCE, 'g');
const MERGE_TOKEN_ANCHORED_RE = new RegExp(`^${MERGE_TOKEN_SOURCE}`);

export interface MergeToken {
  /** 元の文字列中の開始位置。 */
  readonly index: number;
  /** `{{ … }}` を含む生のトークン。 */
  readonly raw: string;
  /** `contact.name` などのパス。 */
  readonly path: string;
  /** `default:` が指定されていればその値（エスケープ解除済み）。無ければ null。 */
  readonly defaultValue: string | null;
}

function unescapeQuoted(value: string): string {
  return value.replace(/\\(.)/g, '$1');
}

/** 文字列から差し込みトークンを列挙する。**文法に合致するものだけ**を返す。 */
export function parseMergeTokens(text: string): MergeToken[] {
  const tokens: MergeToken[] = [];
  MERGE_TOKEN_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = MERGE_TOKEN_RE.exec(text)) !== null) {
    const path = m[1] ?? '';
    const dq = m[2];
    const sq = m[3];
    const bare = m[4];
    const defaultValue =
      dq !== undefined ? unescapeQuoted(dq) : sq !== undefined ? unescapeQuoted(sq) : bare !== undefined ? bare.trim() : null;
    tokens.push({ index: m.index, raw: m[0], path, defaultValue });
  }
  return tokens;
}

/**
 * 文法に合致しない `{{` を列挙する。
 *
 * **これが無いと閉じた文法の意味が半減する。** `{{#if foo}}` のような他エンジンの構文が
 * 「ただの文字列」として素通りし、受信者にそのまま届いてしまう。
 * 未対応構文は黙って通すのではなく、保存の時点で落とす。
 */
export function findMalformedMergeTokens(text: string): Array<{ index: number; snippet: string }> {
  const bad: Array<{ index: number; snippet: string }> = [];
  let from = 0;
  for (;;) {
    const at = text.indexOf('{{', from);
    if (at < 0) break;
    const rest = text.slice(at);
    const matched = MERGE_TOKEN_ANCHORED_RE.exec(rest);
    if (matched) {
      from = at + matched[0].length;
      continue;
    }
    bad.push({ index: at, snippet: rest.slice(0, 40) });
    from = at + 2;
  }
  return bad;
}

/** 文字列に含まれる差し込みキーを列挙する（重複除去なし・出現順）。 */
export function extractMergeKeysFromText(text: string): string[] {
  return parseMergeTokens(text).map((t) => t.path);
}

export type MergeUnknownKeyPolicy = 'throw' | 'empty' | 'keep';

export interface MergeRenderOptions {
  /** キー → 値。`null` / `undefined` / 空文字は「値が無い」として `default:` へ落ちる。 */
  readonly values: Readonly<Record<string, string | number | null | undefined>>;
  /**
   * 差し込み値に適用するエスケープ。**必須**にしてある。
   * 省略可能にすると「HTML に生値を入れる」が既定動作になり、
   * `{{contact.company}}` に `<script>` を入れられる経路が生まれる。
   */
  readonly escape: (value: string) => string;
  /** カタログに無いキーの扱い。既定は `throw`（送信前に必ず気づかせる）。 */
  readonly onUnknownKey?: MergeUnknownKeyPolicy;
  /** 既知キーの集合。省略時は「すべて既知」とみなす。 */
  readonly catalog?: ReadonlySet<string>;
}

export class MergeKeyError extends Error {
  readonly key: string;
  constructor(key: string) {
    super(`未定義の差し込みキーです: {{${key}}}`);
    this.name = 'MergeKeyError';
    this.key = key;
  }
}

/**
 * 差し込みを展開する。
 *
 * 値の決定順は ① `values[path]` が非空 ② `default:` ③ 空文字（または方針に従う）。
 * **`default:` の値もエスケープを通す**（契約者が入力する値なので信用しない）。
 */
export function renderMergeTemplate(template: string, options: MergeRenderOptions): string {
  const { values, escape, catalog } = options;
  const onUnknownKey = options.onUnknownKey ?? 'throw';

  let out = '';
  let cursor = 0;
  for (const token of parseMergeTokens(template)) {
    out += template.slice(cursor, token.index);
    cursor = token.index + token.raw.length;

    if (catalog && !catalog.has(token.path)) {
      if (onUnknownKey === 'throw') throw new MergeKeyError(token.path);
      if (onUnknownKey === 'keep') {
        out += token.raw;
        continue;
      }
      out += '';
      continue;
    }

    const raw = values[token.path];
    const resolved = raw === null || raw === undefined || raw === '' ? token.defaultValue : String(raw);
    out += resolved === null ? '' : escape(resolved);
  }
  out += template.slice(cursor);
  return out;
}

/** HTML 本文・属性値の位置に差し込む。**HTML を組み立てるときは必ずこちらを使う**。 */
export function renderMergeHtml(
  template: string,
  values: MergeRenderOptions['values'],
  extra: Omit<MergeRenderOptions, 'values' | 'escape'> = {},
): string {
  return renderMergeTemplate(template, { ...extra, values, escape: escapeHtml });
}

/** text/plain パートの位置に差し込む。エスケープは不要だが改行の混入だけ潰す。 */
export function renderMergePlain(
  template: string,
  values: MergeRenderOptions['values'],
  extra: Omit<MergeRenderOptions, 'values' | 'escape'> = {},
): string {
  return renderMergeTemplate(template, {
    ...extra,
    values,
    // ヘッダ（Subject）にも使うため、差し込み値由来の CR/LF は必ず落とす。
    escape: (v) => v.replace(/[\r\n]+/g, ' '),
  });
}

/* --------------------------------------------------------- 差し込みカタログ */

/** システムが供給するキー（契約者のリスト属性とは無関係に常に存在する）。 */
export const SYSTEM_MERGE_KEYS = [
  /** 法定フッタ。展開は `legal_footer` ブロックが行う（D17）。 */
  'legal_footer',
  'unsubscribe_url',
  'preferences_url',
  'web_version_url',
  'today',
] as const;

/** `contacts` 表の固定列に対応するキー。 */
export const CONTACT_MERGE_KEYS = [
  'contact.email',
  'contact.displayName',
  'contact.firstName',
  'contact.lastName',
  'contact.company',
] as const;

/** リスト・キャンペーン・組織のメタ。 */
export const CONTEXT_MERGE_KEYS = ['list.name', 'campaign.name', 'campaign.code', 'org.name'] as const;

export const BUILTIN_MERGE_KEYS: readonly string[] = [
  ...SYSTEM_MERGE_KEYS,
  ...CONTACT_MERGE_KEYS,
  ...CONTEXT_MERGE_KEYS,
];

/** リストの `attribute_schema` で宣言された属性キーの接頭辞。 */
export const CONTACT_ATTRIBUTE_PREFIX = 'contact.attributes.';

/**
 * 差し込みカタログを組み立てる。**未定義キーの判定はこの集合が唯一の正**。
 *
 * @param listAttributeKeys `contact_lists.attribute_schema` が宣言した属性キー（接頭辞なし）
 */
export function buildMergeCatalog(listAttributeKeys: readonly string[] = []): ReadonlySet<string> {
  const set = new Set<string>(BUILTIN_MERGE_KEYS);
  for (const key of listAttributeKeys) {
    set.add(`${CONTACT_ATTRIBUTE_PREFIX}${key}`);
  }
  return set;
}

/** カタログに無いキーだけを返す（重複除去済み・昇順）。 */
export function findUnknownMergeKeys(keys: readonly string[], catalog: ReadonlySet<string>): string[] {
  return [...new Set(keys)].filter((k) => !catalog.has(k)).sort();
}

/* =============================================== 法定フッタ（特定電子メール法） */

/**
 * 特定電子メール法 4 条が表示を求める項目。**この 4 つが本設計の「法定 4 項目」**。
 * `contact_lists.legal_footer` jsonb のキーもこの 4 つに固定する（CD §5.2）。
 */
export const LEGAL_FOOTER_FIELDS = ['senderName', 'address', 'unsubscribeContact', 'complaintContact'] as const;
export type LegalFooterField = (typeof LEGAL_FOOTER_FIELDS)[number];

export const legalFooterSchema = z
  .object({
    /** 送信者の氏名または名称（4 条 1 項 1 号）。 */
    senderName: z.string().min(1).max(200),
    /** 住所（4 条 1 項 3 号）。 */
    address: z.string().min(1).max(400),
    /** 受信拒否の通知先（4 条 1 項 2 号）。URL または メールアドレス。 */
    unsubscribeContact: z.string().min(1).max(400),
    /** 苦情・問い合わせの受付先（4 条 1 項 4 号）。 */
    complaintContact: z.string().min(1).max(400),
  })
  .strict();

export type LegalFooter = z.infer<typeof legalFooterSchema>;

export const LEGAL_FOOTER_FIELD_LABELS_JA: Readonly<Record<LegalFooterField, string>> = {
  senderName: '送信者の氏名または名称',
  address: '住所',
  unsubscribeContact: '受信拒否の通知先',
  complaintContact: '苦情・問い合わせ受付先',
};

/** 法定フッタのプレーンテキスト表現。HTML パートとテキストパートで同じ文字列を使う。 */
export function renderLegalFooterLines(footer: LegalFooter): string[] {
  return [
    footer.senderName,
    footer.address,
    `配信停止: ${footer.unsubscribeContact}`,
    `お問い合わせ: ${footer.complaintContact}`,
  ];
}

/** 比較用の正規化。改行・連続空白・全角空白の差で「無い」と誤判定しないため。 */
function normalizeForPresenceCheck(value: string): string {
  return value.replace(/[\s　]+/g, ' ').trim();
}

export interface LegalFooterPresence {
  readonly ok: boolean;
  readonly missingInHtml: LegalFooterField[];
  readonly missingInText: LegalFooterField[];
}

/**
 * **D17 ③ の最終防衛線。** 構造（ブロックの有無）を信用せず、
 * 展開後の HTML と text の両方に法定 4 項目の実文字列が存在するかを見る。
 *
 * UI と API のガードは迂回されうるが、この検査だけは成果物そのものを見るので迂回できない。
 * 呼び出し側（レンダラ）は `ok=false` のとき末尾に自動付与する。
 */
export function checkLegalFooterMaterialized(html: string, text: string, footer: LegalFooter): LegalFooterPresence {
  const normHtml = normalizeForPresenceCheck(html);
  const normText = normalizeForPresenceCheck(text);
  const missingInHtml: LegalFooterField[] = [];
  const missingInText: LegalFooterField[] = [];

  for (const field of LEGAL_FOOTER_FIELDS) {
    const needle = normalizeForPresenceCheck(footer[field]);
    if (needle.length === 0) {
      missingInHtml.push(field);
      missingInText.push(field);
      continue;
    }
    if (!normHtml.includes(needle)) missingInHtml.push(field);
    if (!normText.includes(needle)) missingInText.push(field);
  }

  return { ok: missingInHtml.length === 0 && missingInText.length === 0, missingInHtml, missingInText };
}

/* ============================================================ ブロック JSON */

export const BLOCKS_DOC_VERSION = 'blocks-v1';

/** 保存形式。`raw-html-v1` は上位プラン + 組織の明示有効化が要る（D13）。 */
export const CONTENT_DOC_FORMATS = ['blocks-v1', 'raw-html-v1'] as const;
export type ContentDocFormat = (typeof CONTENT_DOC_FORMATS)[number];

/**
 * MVP のブロック種別。**8 種 + 法定フッタで凍結する**（CD §8.3）。
 * 際限なく増やせる場所なので、増やすときは設計書の改訂を先に行うこと。
 */
export const CONTENT_BLOCK_KINDS = [
  'text',
  'heading',
  'image',
  'button',
  'divider',
  'spacer',
  'html',
  'snippet',
  'legal_footer',
] as const;
export type ContentBlockKind = (typeof CONTENT_BLOCK_KINDS)[number];

/** レイアウトも 3 種で凍結。列数を可変にすると Outlook 用の table 生成が破綻する。 */
export const CONTENT_SECTION_LAYOUTS = ['single', 'two_column', 'three_column'] as const;
export type ContentSectionLayout = (typeof CONTENT_SECTION_LAYOUTS)[number];

export const CONTENT_SECTION_COLUMN_COUNT: Readonly<Record<ContentSectionLayout, number>> = {
  single: 1,
  two_column: 2,
  three_column: 3,
};

const idSchema = z.string().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/, 'ブロック ID は英数字と `_` `-` のみです');
const colorSchema = z.string().regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, '色は #rgb または #rrggbb で指定してください');
const alignSchema = z.enum(['left', 'center', 'right']);

/**
 * ブロックのスタイル。**ホワイトリスト**であり、CSS 文字列は一切受け取らない。
 *
 * `display` / `visibility` / `opacity` を**語彙として持たない**のが要点（D18）。
 * 持たなければ「フッタを display:none で隠す」は構造的に表現できず、
 * linter で後追いする必要が無くなる。
 */
export const blockStyleSchema = z
  .object({
    backgroundColor: colorSchema.optional(),
    textColor: colorSchema.optional(),
    fontSize: z.number().int().min(8).max(72).optional(),
    fontWeight: z.enum(['normal', 'bold']).optional(),
    lineHeight: z.number().min(1).max(3).optional(),
    align: alignSchema.optional(),
    paddingTop: z.number().int().min(0).max(120).optional(),
    paddingRight: z.number().int().min(0).max(120).optional(),
    paddingBottom: z.number().int().min(0).max(120).optional(),
    paddingLeft: z.number().int().min(0).max(120).optional(),
    borderRadius: z.number().int().min(0).max(64).optional(),
    borderColor: colorSchema.optional(),
    borderWidth: z.number().int().min(0).max(16).optional(),
  })
  .strict();

export type BlockStyle = z.infer<typeof blockStyleSchema>;

/**
 * 法定フッタ専用のスタイル。余白と揃えしか触らせない（D18）。
 * `fontSize` / `color` / `backgroundColor` を語彙から外してあるので、
 * 「文字サイズ 0」「背景と同色」による不可視化が表現できない。
 */
export const legalFooterStyleSchema = z
  .object({
    align: alignSchema.optional(),
    paddingTop: z.number().int().min(0).max(120).optional(),
    paddingBottom: z.number().int().min(0).max(120).optional(),
  })
  .strict();

/* ------------------------------------------------------------ インライン */

/** 許可するインラインマーク。TipTap の拡張構成をそのまま写す（D8）。 */
export const INLINE_MARK_TYPES = ['bold', 'italic', 'underline', 'strike'] as const;

export const inlineMarkSchema = z.union([
  z.object({ type: z.enum(INLINE_MARK_TYPES) }).strict(),
  z
    .object({
      type: z.literal('link'),
      href: z.string().min(1).max(2048),
      title: z.string().max(200).optional(),
    })
    .strict(),
]);

/**
 * インラインノード。
 *
 * `mergeVar` を**文字列ではなくノード**にしてあるのが D9 の核心。
 * ノードなら差し込みキーの静的解析が完全になり（文字列走査の取りこぼしが原理的に無い）、
 * エディタ側も atom node として「壊れた `{{`」を作れなくなる。
 */
export const inlineNodeSchema = z.union([
  z
    .object({
      type: z.literal('text'),
      text: z.string().max(20_000),
      marks: z.array(inlineMarkSchema).max(8).optional(),
    })
    .strict(),
  z.object({ type: z.literal('break') }).strict(),
  z
    .object({
      type: z.literal('mergeVar'),
      path: z.string().regex(MERGE_PATH_RE, '差し込みキーの形式が不正です'),
      defaultValue: z.string().max(200).optional(),
    })
    .strict(),
]);

export type InlineNode = z.infer<typeof inlineNodeSchema>;

export const paragraphSchema = z
  .object({
    align: alignSchema.optional(),
    nodes: z.array(inlineNodeSchema).max(500),
  })
  .strict();

/* -------------------------------------------------------------- ブロック */

const baseBlock = { id: idSchema, style: blockStyleSchema.optional() };

export const textBlockSchema = z
  .object({ ...baseBlock, kind: z.literal('text'), paragraphs: z.array(paragraphSchema).min(1).max(200) })
  .strict();

export const headingBlockSchema = z
  .object({
    ...baseBlock,
    kind: z.literal('heading'),
    level: z.union([z.literal(1), z.literal(2), z.literal(3)]),
    nodes: z.array(inlineNodeSchema).min(1).max(100),
  })
  .strict();

export const imageBlockSchema = z
  .object({
    ...baseBlock,
    kind: z.literal('image'),
    assetId: z.string().min(1).max(64),
    /** 空文字を許すが lint が `IMG_NO_ALT` の warning を出す（G2）。 */
    altText: z.string().max(300),
    /** 画像自体をリンクにする場合の遷移先。 */
    href: z.string().max(2048).optional(),
    widthPx: z.number().int().min(16).max(1200).optional(),
  })
  .strict();

export const buttonBlockSchema = z
  .object({
    ...baseBlock,
    kind: z.literal('button'),
    label: z.string().min(1).max(200),
    href: z.string().min(1).max(2048),
  })
  .strict();

export const dividerBlockSchema = z.object({ ...baseBlock, kind: z.literal('divider') }).strict();

export const spacerBlockSchema = z
  .object({ ...baseBlock, kind: z.literal('spacer'), heightPx: z.number().int().min(1).max(200) })
  .strict();

/**
 * 生 HTML ブロック。**上位プラン + 組織の明示有効化があるときだけ受理する**（D13）。
 * zod で受理してもよいかの判定は `validateBlocksDocument()` の `allowHtmlSource` で行う。
 */
export const htmlBlockSchema = z
  .object({ ...baseBlock, kind: z.literal('html'), html: z.string().max(200_000) })
  .strict();

/** 他文書（`kind='snippet'`）の参照。**継承ではなく参照**で、展開はビルド時に行う。 */
export const snippetBlockSchema = z
  .object({
    ...baseBlock,
    kind: z.literal('snippet'),
    snippetDocumentId: z.string().min(1).max(64),
    /** 固定したい版。省略時はビルド時点の最新（`content_snippet_uses` に記録される）。 */
    snippetRevision: z.number().int().positive().optional(),
  })
  .strict();

/**
 * 法定フッタ。**本文を持たない**のが要点。
 * 実際の文言は `contact_lists.legal_footer` からビルド時に供給されるので、
 * 契約者はブロックの中身を編集できない（編集できるのはリスト側の 4 項目）。
 */
export const legalFooterBlockSchema = z
  .object({
    id: idSchema,
    kind: z.literal('legal_footer'),
    style: legalFooterStyleSchema.optional(),
    /** フッタ前に置ける任意の追記（例: 「本メールは〜に配信しています」）。 */
    note: z.string().max(500).optional(),
  })
  .strict();

export const contentBlockSchema = z.discriminatedUnion('kind', [
  textBlockSchema,
  headingBlockSchema,
  imageBlockSchema,
  buttonBlockSchema,
  dividerBlockSchema,
  spacerBlockSchema,
  htmlBlockSchema,
  snippetBlockSchema,
  legalFooterBlockSchema,
]);

export type ContentBlock = z.infer<typeof contentBlockSchema>;

export const contentColumnSchema = z
  .object({ blocks: z.array(contentBlockSchema).max(100) })
  .strict();

export const contentSectionSchema = z
  .object({
    id: idSchema,
    layout: z.enum(CONTENT_SECTION_LAYOUTS),
    style: blockStyleSchema.optional(),
    columns: z.array(contentColumnSchema).min(1).max(3),
  })
  .strict()
  .superRefine((section, ctx) => {
    const expected = CONTENT_SECTION_COLUMN_COUNT[section.layout];
    if (section.columns.length !== expected) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `レイアウト ${section.layout} の列数は ${expected} です（実際: ${section.columns.length}）`,
        path: ['columns'],
      });
    }
  });

export type ContentSection = z.infer<typeof contentSectionSchema>;

export const documentSettingsSchema = z
  .object({
    contentWidthPx: z.number().int().min(320).max(800).default(600),
    backgroundColor: colorSchema.optional(),
    bodyBackgroundColor: colorSchema.optional(),
    textColor: colorSchema.optional(),
    linkColor: colorSchema.optional(),
    fontFamily: z.string().max(200).optional(),
  })
  .strict();

/**
 * `blocks-v1` の文書全体。**S3 に置く `doc.json` の中身そのもの**。
 *
 * `subject` / `preheader` を持たせてあるのは、S3 のオブジェクト単体で
 * エクスポート・インポートが往復できるようにするため。
 * DB 側にも同名の列があるが、**正は DB 側**（検索の対象になるのは DB の列だけ）。
 */
export const blocksDocumentSchema = z
  .object({
    version: z.literal(BLOCKS_DOC_VERSION),
    subject: z.string().max(500).optional(),
    preheader: z.string().max(500).optional(),
    settings: documentSettingsSchema,
    sections: z.array(contentSectionSchema).min(1).max(100),
  })
  .strict();

export type BlocksDocument = z.infer<typeof blocksDocumentSchema>;

/* ----------------------------------------------------------- 走査ユーティリティ */

export interface BlockRef {
  readonly sectionIndex: number;
  readonly columnIndex: number;
  readonly blockIndex: number;
  readonly block: ContentBlock;
  /** 人が読める位置（`sections[0].columns[1].blocks[2]`）。issue の `path` に使う。 */
  readonly path: string;
}

/** 文書中の全ブロックを出現順に列挙する。 */
export function* iterateBlocks(doc: BlocksDocument): Generator<BlockRef> {
  for (let s = 0; s < doc.sections.length; s += 1) {
    const section = doc.sections[s];
    if (!section) continue;
    for (let c = 0; c < section.columns.length; c += 1) {
      const column = section.columns[c];
      if (!column) continue;
      for (let b = 0; b < column.blocks.length; b += 1) {
        const block = column.blocks[b];
        if (!block) continue;
        yield {
          sectionIndex: s,
          columnIndex: c,
          blockIndex: b,
          block,
          path: `sections[${s}].columns[${c}].blocks[${b}]`,
        };
      }
    }
  }
}

/** ブロックが持つ「差し込みが効く文字列」を列挙する。 */
function* textFieldsOf(block: ContentBlock): Generator<string> {
  switch (block.kind) {
    case 'text':
      for (const p of block.paragraphs) {
        for (const n of p.nodes) if (n.type === 'text') yield n.text;
      }
      return;
    case 'heading':
      for (const n of block.nodes) if (n.type === 'text') yield n.text;
      return;
    case 'image':
      yield block.altText;
      if (block.href) yield block.href;
      return;
    case 'button':
      yield block.label;
      yield block.href;
      return;
    case 'html':
      yield block.html;
      return;
    case 'legal_footer':
      if (block.note) yield block.note;
      return;
    default:
      return;
  }
}

function* mergeVarNodesOf(block: ContentBlock): Generator<InlineNode & { type: 'mergeVar' }> {
  const nodes: InlineNode[] =
    block.kind === 'text'
      ? block.paragraphs.flatMap((p) => p.nodes)
      : block.kind === 'heading'
        ? block.nodes
        : [];
  for (const n of nodes) if (n.type === 'mergeVar') yield n;
}

/**
 * 文書が使っている差し込みキーを重複なく昇順で返す。
 * `content_artifacts.merge_keys` に入れる値の出典はここ。
 *
 * atom node（D9）と文字列中のトークンの**両方**を見る。
 * 文字列側も見るのは、raw HTML ブロックと API 直投稿では atom 化されないため。
 */
export function extractMergeKeys(doc: BlocksDocument): string[] {
  const keys = new Set<string>();
  if (doc.subject) for (const k of extractMergeKeysFromText(doc.subject)) keys.add(k);
  if (doc.preheader) for (const k of extractMergeKeysFromText(doc.preheader)) keys.add(k);
  for (const { block } of iterateBlocks(doc)) {
    for (const node of mergeVarNodesOf(block)) keys.add(node.path);
    for (const text of textFieldsOf(block)) {
      for (const k of extractMergeKeysFromText(text)) keys.add(k);
    }
  }
  return [...keys].sort();
}

/**
 * 文書中のリンク URL を列挙する（重複除去済み）。
 *
 * eTLD+1 への丸めは `toRegistrableDomain()` が行う（同じファイルのすぐ下）。
 * **自前で「最後の 2 ラベル」に丸めてはいけない。** `co.jp` / `github.io` /
 * `s3.amazonaws.com` のような多段の公開接尾辞が出て、
 * 「別の契約者のサイト」と「同じ登録ドメイン」の区別が壊れる。
 */
export function extractLinkUrls(doc: BlocksDocument): string[] {
  const urls = new Set<string>();
  const push = (v: string | undefined) => {
    if (v && v.trim().length > 0) urls.add(v.trim());
  };
  for (const { block } of iterateBlocks(doc)) {
    if (block.kind === 'button') push(block.href);
    if (block.kind === 'image') push(block.href);
    if (block.kind === 'text') {
      for (const p of block.paragraphs) {
        for (const n of p.nodes) {
          if (n.type !== 'text' || !n.marks) continue;
          for (const mark of n.marks) if (mark.type === 'link') push(mark.href);
        }
      }
    }
    if (block.kind === 'heading') {
      for (const n of block.nodes) {
        if (n.type !== 'text' || !n.marks) continue;
        for (const mark of n.marks) if (mark.type === 'link') push(mark.href);
      }
    }
  }
  return [...urls].sort();
}

/**
 * URL（または裸のホスト名）を **eTLD+1（登録可能ドメイン）** に丸める。
 *
 * `content_artifacts.link_domains` と運営者の異常検知が見るのはこの値だけで、
 * 生 URL は DB に置かない（本文に準じる情報なので）。
 *
 * 実装は `tldts`（公開接尾辞リストを同梱する実在パッケージ）に委ねる。
 * **自前の「最後の 2 ラベル」実装は必ず間違う**:
 *   - `www.example.co.jp` → 正: `example.co.jp` / 誤: `co.jp`
 *   - `a.b.c.example.jp` → 正: `example.jp`
 * 誤ると「無関係な契約者のサイトを同一ドメイン扱いする」ので、
 * 到達性の異常検知が別の契約者の評判に反応する。
 *
 * 判定できない入力（`mailto:` / `tel:` / IP リテラル / 単一ラベル）は `null`。
 * **`null` を「不明なドメイン」として保存しないこと。** 落とすのが正しい。
 */
export function toRegistrableDomain(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;

  let host = trimmed;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed)) {
    // スキーム付き。`mailto:` / `tel:` はホストを持たないのでここで落ちる。
    let parsed: URL;
    try {
      parsed = new URL(trimmed);
    } catch {
      return null;
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    host = parsed.hostname;
  }
  if (host.length === 0) return null;

  // `allowPrivateDomains` は**既定（false）のまま**使う。
  // ここを true にすると `github.io` のような PSL の PRIVATE セクションまで
  // 接尾辞として扱われ、`user.github.io` が 1 ドメイン扱いになる。
  // apps/worker の `toRegistrableDomains()`（`content_artifacts.link_domains` を
  // 実際に書く側）が既定で動いているので、**そちらと必ず同じ値**を返すことが
  // この関数の存在意義（CP の検査とレンダラの記録がずれたら検査の意味が無い）。
  const domain = getDomain(host);
  return domain === null || domain.length === 0 ? null : domain.toLowerCase();
}

/**
 * 文書中のリンク先を eTLD+1 に丸めて列挙する（重複除去・昇順）。
 * `content_artifacts.link_domains` に入れる値そのもの。
 */
export function extractLinkDomains(doc: BlocksDocument): string[] {
  const domains = new Set<string>();
  for (const url of extractLinkUrls(doc)) {
    const domain = toRegistrableDomain(url);
    if (domain) domains.add(domain);
  }
  return [...domains].sort();
}

/** 参照しているアセット ID（`content_asset_usages` の出典）。 */
export function extractAssetIds(doc: BlocksDocument): string[] {
  const ids = new Set<string>();
  for (const { block } of iterateBlocks(doc)) {
    if (block.kind === 'image') ids.add(block.assetId);
  }
  return [...ids].sort();
}

/** 参照しているスニペット文書 ID（`content_snippet_uses` の出典）。 */
export function extractSnippetDocumentIds(doc: BlocksDocument): string[] {
  const ids = new Set<string>();
  for (const { block } of iterateBlocks(doc)) {
    if (block.kind === 'snippet') ids.add(block.snippetDocumentId);
  }
  return [...ids].sort();
}

/* ============================================================ 文書の検証 */

export const CONTENT_ISSUE_SEVERITIES = ['blocking', 'warning', 'info'] as const;
export type ContentIssueSeverity = (typeof CONTENT_ISSUE_SEVERITIES)[number];

/**
 * lint / validate の symbol。**`content_artifacts.lint` jsonb に入るのは
 * この symbol と件数だけ**（本文断片を入れない・D37）。
 */
export const CONTENT_ISSUE_CODES = [
  'SCHEMA_INVALID',
  'LEGAL_FOOTER_MISSING',
  'LEGAL_FOOTER_DUPLICATED',
  'LEGAL_FOOTER_NOT_LAST',
  'MERGE_SYNTAX_INVALID',
  'MERGE_KEY_UNKNOWN',
  'HTML_SOURCE_NOT_ALLOWED',
  'LINK_URL_INVALID',
  'IMG_NO_ALT',
  'BUTTON_LABEL_EMPTY',
  'HIDDEN_CONTENT',
  'HTML_TOO_LARGE',
  'IMAGE_ONLY',
  'SNIPPET_OUTDATED',
] as const;
export type ContentIssueCode = (typeof CONTENT_ISSUE_CODES)[number];

export interface ContentIssue {
  readonly severity: ContentIssueSeverity;
  readonly code: ContentIssueCode;
  readonly message: string;
  readonly path?: string;
}

export interface ValidateBlocksOptions {
  /** 生 HTML ブロックを許すか（プラン機能 + `org_content_policies.allow_html_source` の AND）。 */
  readonly allowHtmlSource?: boolean;
  /** 差し込みカタログ。省略時は未定義キー検査を行わない（リスト未確定の下書き段階）。 */
  readonly mergeCatalog?: ReadonlySet<string>;
}

export interface ValidateBlocksResult {
  readonly ok: boolean;
  readonly doc: BlocksDocument | null;
  readonly issues: ContentIssue[];
}

/** 生成 HTML の上限（D16）。Gmail のクリップ（約 102KB）に安全マージンを取った値。 */
export const CONTENT_HTML_MAX_BYTES = 98 * 1024;

/**
 * 不可視化の手口を検出する（raw HTML ブロック用・D18）。
 * ブロック JSON 側はスタイル語彙から `display` 等を外してあるので検査不要。
 */
export function findHiddenContentPatterns(html: string): ContentIssueCode[] {
  const found: ContentIssueCode[] = [];
  if (/display\s*:\s*none/i.test(html)) found.push('HIDDEN_CONTENT');
  else if (/font-size\s*:\s*0(?:px|pt|em|%)?\b/i.test(html)) found.push('HIDDEN_CONTENT');
  else if (/mso-hide\s*:\s*all/i.test(html)) found.push('HIDDEN_CONTENT');
  else if (/visibility\s*:\s*hidden/i.test(html)) found.push('HIDDEN_CONTENT');
  return found;
}

/**
 * ブロック JSON を検証する。**API の保存ハンドラが最初に通す関数**。
 *
 * 構造の検証（zod）と業務ルールの検証（法定フッタ・差し込みキー・プラン）を
 * 1 か所に集めてある。片方だけを呼ぶ経路を作ると必ず抜ける。
 */
export function validateBlocksDocument(input: unknown, options: ValidateBlocksOptions = {}): ValidateBlocksResult {
  const parsed = blocksDocumentSchema.safeParse(input);
  if (!parsed.success) {
    return {
      ok: false,
      doc: null,
      issues: parsed.error.issues.map((issue) => ({
        severity: 'blocking' as const,
        code: 'SCHEMA_INVALID' as const,
        message: issue.message,
        path: issue.path.join('.'),
      })),
    };
  }

  const doc = parsed.data;
  const issues: ContentIssue[] = [];
  const blocks = [...iterateBlocks(doc)];

  /* --- 法定フッタ: ちょうど 1 個・最後（D17 ②） --- */
  const footers = blocks.filter((b) => b.block.kind === 'legal_footer');
  if (footers.length === 0) {
    issues.push({
      severity: 'blocking',
      code: 'LEGAL_FOOTER_MISSING',
      message: '法定フッタのブロックがありません。末尾に 1 個だけ配置してください',
    });
  } else if (footers.length > 1) {
    issues.push({
      severity: 'blocking',
      code: 'LEGAL_FOOTER_DUPLICATED',
      message: `法定フッタのブロックが ${footers.length} 個あります。1 個だけにしてください`,
      path: footers[1]?.path,
    });
  } else {
    const last = blocks[blocks.length - 1];
    if (last && last.block.kind !== 'legal_footer') {
      issues.push({
        severity: 'blocking',
        code: 'LEGAL_FOOTER_NOT_LAST',
        message: '法定フッタは本文の末尾に置いてください',
        path: footers[0]?.path,
      });
    }
  }

  /* --- 差し込み文法・未定義キー・URL・アクセシビリティ --- */
  const allMergeKeys = new Set<string>();

  const scanText = (text: string, path: string) => {
    for (const bad of findMalformedMergeTokens(text)) {
      issues.push({
        severity: 'blocking',
        code: 'MERGE_SYNTAX_INVALID',
        message: `差し込みの書き方が不正です（位置 ${bad.index}）。使えるのは {{ キー }} と {{ キー | default:"既定値" }} だけです`,
        path,
      });
    }
    for (const key of extractMergeKeysFromText(text)) allMergeKeys.add(key);
  };

  if (doc.subject) scanText(doc.subject, 'subject');
  if (doc.preheader) scanText(doc.preheader, 'preheader');

  let hasVisibleText = false;
  let imageCount = 0;

  for (const ref of blocks) {
    const { block, path } = ref;

    for (const node of mergeVarNodesOf(block)) allMergeKeys.add(node.path);
    for (const text of textFieldsOf(block)) scanText(text, path);

    if (block.kind === 'html') {
      if (!options.allowHtmlSource) {
        issues.push({
          severity: 'blocking',
          code: 'HTML_SOURCE_NOT_ALLOWED',
          message: 'HTML の直接編集はこのプラン・この組織では利用できません',
          path,
        });
      }
      for (const code of findHiddenContentPatterns(block.html)) {
        issues.push({
          severity: 'warning',
          code,
          message: '不可視のコンテンツ（display:none / font-size:0 など）が含まれています',
          path,
        });
      }
      if (block.html.replace(/<[^>]*>/g, '').trim().length > 0) hasVisibleText = true;
    }

    if (block.kind === 'image') {
      imageCount += 1;
      if (block.altText.trim().length === 0) {
        issues.push({
          severity: 'warning',
          code: 'IMG_NO_ALT',
          message: '画像に代替テキスト（alt）がありません。画像を表示しない受信者に内容が伝わりません',
          path,
        });
      }
      if (block.href !== undefined && !isLinkAcceptable(block.href)) {
        issues.push({ severity: 'blocking', code: 'LINK_URL_INVALID', message: 'リンク先の URL が不正です', path });
      }
    }

    if (block.kind === 'button') {
      if (!isLinkAcceptable(block.href)) {
        issues.push({ severity: 'blocking', code: 'LINK_URL_INVALID', message: 'ボタンのリンク先が不正です', path });
      }
      if (block.label.trim().length === 0) {
        issues.push({ severity: 'blocking', code: 'BUTTON_LABEL_EMPTY', message: 'ボタンの文言が空です', path });
      }
      hasVisibleText = true;
    }

    if (block.kind === 'text' || block.kind === 'heading') {
      const nodes = block.kind === 'text' ? block.paragraphs.flatMap((p) => p.nodes) : block.nodes;
      for (const node of nodes) {
        if (node.type === 'mergeVar') {
          // 差し込みは実行時に文字が入るので「テキストがある」と数える。
          hasVisibleText = true;
          continue;
        }
        if (node.type !== 'text') continue;
        if (node.text.trim().length > 0) hasVisibleText = true;
        for (const mark of node.marks ?? []) {
          if (mark.type === 'link' && !isLinkAcceptable(mark.href)) {
            issues.push({ severity: 'blocking', code: 'LINK_URL_INVALID', message: 'リンク先の URL が不正です', path });
          }
        }
      }
    }
  }

  if (options.mergeCatalog) {
    for (const key of findUnknownMergeKeys([...allMergeKeys], options.mergeCatalog)) {
      issues.push({
        severity: 'blocking',
        code: 'MERGE_KEY_UNKNOWN',
        message: `差し込みキー {{${key}}} は宛先リストで定義されていません`,
      });
    }
  }

  // 画像だけのメールはスパム判定を強く受ける。送信は止めないが必ず知らせる。
  if (imageCount > 0 && !hasVisibleText) {
    issues.push({
      severity: 'warning',
      code: 'IMAGE_ONLY',
      message: '本文が画像のみです。テキストを含めないと迷惑メール判定を受けやすくなります',
    });
  }

  return { ok: !issues.some((i) => i.severity === 'blocking'), doc, issues };
}

/**
 * リンク先として受理してよいか。
 * **差し込み変数だけで構成された URL は通す**（`{{unsubscribe_url}}` は実行時に確定する）。
 */
function isLinkAcceptable(href: string): boolean {
  const trimmed = href.trim();
  if (trimmed.length === 0) return false;
  const tokens = parseMergeTokens(trimmed);
  if (tokens.length > 0) {
    // 差し込みを含む URL は、差し込みを取り除いた残りが不正なスキームでないことだけ見る。
    const stripped = renderMergeTemplate(trimmed, { values: {}, escape: (v) => v, onUnknownKey: 'empty' });
    if (stripped.trim().length === 0) return true;
    return isSafeUrl(stripped) || /^https?:\/\//i.test(stripped);
  }
  return isSafeUrl(trimmed);
}

/**
 * 生成した HTML のサイズ判定（D16）。
 * quoted-printable 展開後のバイト数で見るので、呼び出し側は展開後の文字列を渡すこと。
 */
export function checkHtmlSize(html: string): ContentIssue | null {
  const bytes = new TextEncoder().encode(html).length;
  if (bytes <= CONTENT_HTML_MAX_BYTES) return null;
  return {
    severity: 'blocking',
    code: 'HTML_TOO_LARGE',
    message: `本文が ${Math.round(bytes / 1024)}KB あります。${Math.round(
      CONTENT_HTML_MAX_BYTES / 1024,
    )}KB を超えると Gmail で末尾が省略され、法定フッタが受信者から見えなくなります`,
  };
}

/** 空の `blocks-v1` 文書（新規作成時の初期値）。法定フッタを最初から入れておく。 */
export function emptyBlocksDocument(): BlocksDocument {
  return {
    version: BLOCKS_DOC_VERSION,
    settings: { contentWidthPx: 600 },
    sections: [
      {
        id: 's1',
        layout: 'single',
        columns: [
          {
            blocks: [
              { id: 'b1', kind: 'text', paragraphs: [{ nodes: [{ type: 'text', text: '' }] }] },
              { id: 'bf', kind: 'legal_footer' },
            ],
          },
        ],
      },
    ],
  };
}

/* ================================================ 受信者向けリンク（公開面） */

/**
 * 受信者に配るリンクの参照値（`/u/` `/c/` `/m/`）。
 *
 * **`campaignId` を必ず参照に含める**のが要点。`campaign_recipients` は
 * `campaign_id` のハッシュ 16 分割で、一意インデックスも
 * `(campaign_id, recipient_token)` なので、トークン単体で引くと
 * 16 パーティションの全走査になる。逆に言えば、参照に campaignId を
 * 入れておけば**索引 1 発で引ける**。
 *
 * **署名を足さない。** `recipient_token` 自体が
 * `HMAC-SHA256(UNSUB_SIGNING_KEY, campaignId + '.' + contactId)` の先頭 12B で、
 * 総当たりは 96 ビット。二重に署名しても強度は上がらず、
 * 「鍵を API 側にも配る」という運用上の穴が増えるだけになる。
 * 受け口は**表を引いて一致した行があるかどうか**だけで判定する。
 *
 * 区切りは `.`（ULID にも base64url にも現れない文字）。
 * `-` と `_` は base64url が使うので区切りに使えない。
 */
export const RECIPIENT_REF_SEPARATOR = '.';

/** ULID（Crockford Base32・26 桁）。`newId()` の出力と同じ形。 */
const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/;
/** `recipient_token`（HMAC 12B の base64url = 16 文字）。 */
const RECIPIENT_TOKEN_RE = /^[A-Za-z0-9_-]{8,64}$/;

export interface RecipientRef {
  campaignId: string;
  recipientToken: string;
}

/** `/u/` `/m/` に載せる参照値を組み立てる。 */
export function encodeRecipientRef(campaignId: string, recipientToken: string): string {
  return `${campaignId}${RECIPIENT_REF_SEPARATOR}${recipientToken}`;
}

/**
 * 参照値を分解する。**形が違うものは全て `null`。**
 *
 * ここで緩く受けると、受け口側が「見つからない」と「壊れている」を
 * 区別できなくなり、404 を返すべき経路で 500 が出る。
 */
export function parseRecipientRef(ref: string): RecipientRef | null {
  const parts = ref.split(RECIPIENT_REF_SEPARATOR);
  if (parts.length !== 2) return null;
  const [campaignId, recipientToken] = parts as [string, string];
  if (!ULID_RE.test(campaignId)) return null;
  if (!RECIPIENT_TOKEN_RE.test(recipientToken)) return null;
  return { campaignId, recipientToken };
}

export interface ClickRef extends RecipientRef {
  linkIndex: number;
}

/**
 * クリックリダイレクタの参照値（D25）。
 *
 * **リダイレクト先の URL は入れない。** 埋めると、署名していても
 * 我々のドメインがオープンリダイレクタとして機能する。入るのは
 * `campaign_links` を引くための `(campaignId, linkIndex)` と、
 * クリックを誰に紐付けるかの `recipientToken` だけ。
 */
export function encodeClickRef(
  campaignId: string,
  linkIndex: number,
  recipientToken: string,
): string {
  return [campaignId, String(linkIndex), recipientToken].join(RECIPIENT_REF_SEPARATOR);
}

export function parseClickRef(ref: string): ClickRef | null {
  const parts = ref.split(RECIPIENT_REF_SEPARATOR);
  if (parts.length !== 3) return null;
  const [campaignId, rawIndex, recipientToken] = parts as [string, string, string];
  if (!ULID_RE.test(campaignId)) return null;
  if (!/^\d{1,6}$/.test(rawIndex)) return null;
  if (!RECIPIENT_TOKEN_RE.test(recipientToken)) return null;
  return { campaignId, linkIndex: Number(rawIndex), recipientToken };
}

/**
 * 受信者向け公開エンドポイントのパス。
 *
 * **CP とノード（`mailsv-bulkinject`）が同じ値を組み立てる**必要があるので、
 * 文字列を両側に書かず、ここ 1 か所を参照する（D45 と同じ理由）。
 */
export const PUBLIC_RECIPIENT_PATH = {
  /** 設定センター（GET）と One-Click 解除（POST）。 */
  unsubscribe: '/api/v1/public/u',
  /** クリックリダイレクタ。 */
  click: '/api/v1/public/c',
  /** Web 版（ブラウザで表示）。 */
  webVersion: '/api/v1/public/m',
  /** ダブルオプトインの確認着地点。 */
  optinConfirm: '/api/v1/public/optin',
  /** 申込フォームの配信と受理。 */
  signupForm: '/api/v1/public/f',
  /** リスト単位の公開アーカイブ（`archive_public_id` で引く）。 */
  archive: '/api/v1/public/a',
} as const;

function joinPublicUrl(baseUrl: string, path: string, ref: string): string {
  return `${baseUrl.replace(/\/+$/, '')}${path}/${encodeURIComponent(ref)}`;
}

/** `{{unsubscribe_url}}` の実値。One-Click の POST 先も同じ URL。 */
export function buildUnsubscribeUrl(baseUrl: string, ref: string): string {
  return joinPublicUrl(baseUrl, PUBLIC_RECIPIENT_PATH.unsubscribe, ref);
}

/**
 * `{{preferences_url}}` の実値。
 *
 * **解除 URL と同一にしてある。** GET が設定センター、POST が即時解除という
 * 分け方なので、別 URL を作ると「どちらを踏んでも同じ画面」が 2 本並ぶだけになる。
 */
export function buildPreferencesUrl(baseUrl: string, ref: string): string {
  return buildUnsubscribeUrl(baseUrl, ref);
}

/** `{{web_version_url}}`（ブラウザで表示）。 */
export function buildWebVersionUrl(baseUrl: string, ref: string): string {
  return joinPublicUrl(baseUrl, PUBLIC_RECIPIENT_PATH.webVersion, ref);
}

/** クリックリダイレクタの URL。 */
export function buildClickUrl(baseUrl: string, ref: string): string {
  return joinPublicUrl(baseUrl, PUBLIC_RECIPIENT_PATH.click, ref);
}

/**
 * 受信者トークンを本文へ持ち込むための差し込みキー。
 *
 * **カタログ（`BUILTIN_MERGE_KEYS`）には載せない。** 契約者に見せる
 * キーではなく、ビルド時にこちらが本文へ埋め込む内部の穴である。
 * カタログに載せると、契約者が本文の見えるところへ
 * `{{recipient.token}}` と書けてしまい、転送されたメールから
 * 他人の設定センターを開ける URL が本文に露出する。
 */
export const RECIPIENT_TOKEN_MERGE_KEY = 'recipient.token';

/**
 * アーカイブ（`/a/`）で使う受信者トークンの代替値。
 *
 * アーカイブには受信者が居ないので、本文中のクリックリダイレクタに
 * 埋めるトークンが無い。空にすると `parseClickRef()` が落ちて
 * **アーカイブ本文のリンクが全部 404** になるため、
 * 「実在しないが形は正しい」値を入れて**リダイレクトだけ通す**
 * （`campaign_recipients` を引けないので計測は行われない）。
 */
export const ARCHIVE_RECIPIENT_TOKEN = 'archive-view';

/**
 * 本文へ焼き込むクリックリダイレクタの href。
 *
 * **受信者ごとの値は差し込みトークンとして残す。** 成果物 HTML は
 * キャンペーン単位で 1 つしか作らないので、ここで受信者を確定できない。
 * 1 通を組み立てる側（ノードの `mailsv-bulkinject` / Web 版 / アーカイブ）が
 * `RECIPIENT_TOKEN_MERGE_KEY` に値を与えることで初めて URL が完成する。
 */
export function buildClickHrefTemplate(
  baseUrl: string,
  campaignId: string,
  linkIndex: number,
): string {
  const prefix = `${baseUrl.replace(/\/+$/, '')}${PUBLIC_RECIPIENT_PATH.click}/`;
  return `${prefix}${campaignId}${RECIPIENT_REF_SEPARATOR}${linkIndex}${RECIPIENT_REF_SEPARATOR}{{${RECIPIENT_TOKEN_MERGE_KEY}}}`;
}

export interface PublicRenderValuesInput {
  publicBaseUrl: string;
  campaignId: string;
  /**
   * 受信者が確定しているか。Web 版（`/m/:ref`）は参照値から確定するので
   * 実トークン、アーカイブ（`/a/`）は受信者が居ないので `null`。
   */
  recipientToken: string | null;
  campaignName: string;
  listName: string;
  orgName: string;
  /**
   * 受信者が確定しないときの解除導線の行き先（アーカイブ索引）。
   * `null` なら解除リンクは空になる。
   */
  fallbackUrl?: string | null;
  now?: Date;
}

/**
 * 公開ページ（Web 版・アーカイブ）で成果物 HTML を描くときの差し込み値。
 *
 * **`contact.*` を 1 つも入れない。** URL を共有された瞬間に第三者へ
 * 氏名や属性が渡るため。入れないことで `{{contact.displayName}}` は
 * `default:` の値（無ければ空）に落ちる。
 *
 * 逆に、**素の `{{…}}` を残したまま返してはいけない。** 残すと
 * 受信者にはテンプレートの中括弧がそのまま見え、かつ
 * `{{unsubscribe_url}}` が URL にならないので**Web 版からは配信停止
 * できない**（特定電子メール法 3 条 3 項の導線が 1 本落ちる）。
 */
export function buildPublicRenderValues(
  input: PublicRenderValuesInput,
): Record<string, string> {
  const now = input.now ?? new Date();
  const ref =
    input.recipientToken === null
      ? null
      : encodeRecipientRef(input.campaignId, input.recipientToken);

  const fallback = input.fallbackUrl ?? '';
  return {
    unsubscribe_url: ref ? buildUnsubscribeUrl(input.publicBaseUrl, ref) : fallback,
    preferences_url: ref ? buildPreferencesUrl(input.publicBaseUrl, ref) : fallback,
    web_version_url: ref ? buildWebVersionUrl(input.publicBaseUrl, ref) : fallback,
    today: now.toISOString().slice(0, 10),
    'campaign.name': input.campaignName,
    'campaign.code': input.campaignId,
    'list.name': input.listName,
    'org.name': input.orgName,
    // クリックリダイレクタの href を完成させる穴。アーカイブでは
    // 実在しない代替値を入れ、リダイレクトだけ通して計測は行わない。
    [RECIPIENT_TOKEN_MERGE_KEY]: input.recipientToken ?? ARCHIVE_RECIPIENT_TOKEN,
  };
}

/**
 * プレビュー・テスト送信・共有リンクで成果物 HTML を出すときの最小の補完。
 *
 * **受信者トークンだけを埋め、他の差し込みは素のまま残す。**
 * これらの面は「テンプレートを確認する」ためのものなので、
 * `{{contact.displayName}}` が中括弧のまま見えるのが正しい
 * （実配信で何が差し込まれるかを確認できる）。
 *
 * 一方でクリックリダイレクタの href だけは、受信者部分が埋まらないと
 * **URL として壊れて踏めない**。計測が有効なキャンペーンをプレビューした
 * 瞬間に本文のリンクが全部死ぬ、という形で顕在化するので、
 * ここだけは実在しない代替トークンで URL の形を成立させる
 * （踏むとリダイレクトはするが、計測はされない）。
 */
export function fillClickTokensForPreview(html: string): string {
  return renderMergeHtml(
    html,
    { [RECIPIENT_TOKEN_MERGE_KEY]: ARCHIVE_RECIPIENT_TOKEN },
    { onUnknownKey: 'keep', catalog: new Set([RECIPIENT_TOKEN_MERGE_KEY]) },
  );
}

/** リスト単位の公開アーカイブ（`/a/<archivePublicId>`）。 */
export function buildArchiveIndexUrl(baseUrl: string, archivePublicId: string): string {
  return joinPublicUrl(baseUrl, PUBLIC_RECIPIENT_PATH.archive, archivePublicId);
}

/** アーカイブ内の 1 配信（`/a/<archivePublicId>/<campaignPublicId>`）。 */
export function buildArchiveEntryUrl(
  baseUrl: string,
  archivePublicId: string,
  campaignPublicId: string,
): string {
  return `${buildArchiveIndexUrl(baseUrl, archivePublicId)}/${encodeURIComponent(campaignPublicId)}`;
}

/** ダブルオプトインの確認 URL（確認メールに載せる）。 */
export function buildOptinConfirmUrl(baseUrl: string, token: string): string {
  return joinPublicUrl(baseUrl, PUBLIC_RECIPIENT_PATH.optinConfirm, token);
}

/* ------------------------------------------- List-Unsubscribe（RFC 8058） */

export interface ListUnsubscribeInput {
  /** `/u/<ref>` の絶対 URL。 */
  unsubscribeUrl: string;
  /**
   * `mailto:` 解除の宛先（VERP のバウンス受け口とは別）。無ければ省略。
   *
   * **「受け取れるアドレス」以外を入れないこと。** ヘッダに書けることと
   * そのアドレスでメールを受けられることは別で、いまの唯一の呼び出し元
   * （`apps/agent/src/bulk/mime.ts`）は **`null` を渡すのが正しい**。
   * ノード側に解除メールの受け口（`unsub-ingest`）がまだ無く、
   * 入れると Postfix が `bounce-ingest` へ回して**黙って捨てる**ためで、
   * 理由と必要な作業 4 点は呼び出し元のコメントに書いてある。
   * 「受け口はあるのに空だ」と読み違えて埋めない。
   */
  mailtoAddress?: string | null;
  /**
   * One-Click（RFC 8058）を宣言するか。
   *
   * **トランザクションメールには付けない。** パスワード再設定の通知に
   * 「1 クリックで解除」を付けると、受信者が解除したつもりでも
   * 送信は止まらない（そもそも購読ではない）ので、
   * 「解除したのに届く」という最悪の体験になる。一斉配信だけに付ける。
   */
  oneClick: boolean;
}

/**
 * `List-Unsubscribe` / `List-Unsubscribe-Post` の値を組み立てる。
 *
 * **`oneClick` のとき URL は必ず先頭**にする。RFC 8058 は
 * 「One-Click の POST 先は List-Unsubscribe の**最初の HTTPS URI**」と
 * 定めており、`mailto:` を先に書くと主要な受信側が One-Click を無効にする。
 */
export function buildListUnsubscribeHeaders(
  input: ListUnsubscribeInput,
): Record<string, string> {
  const targets = [`<${input.unsubscribeUrl}>`];
  if (input.mailtoAddress) targets.push(`<mailto:${input.mailtoAddress}>`);
  const headers: Record<string, string> = { 'List-Unsubscribe': targets.join(', ') };
  if (input.oneClick) {
    headers['List-Unsubscribe-Post'] = 'List-Unsubscribe=One-Click';
  }
  return headers;
}

/**
 * One-Click の POST 本文が RFC 8058 のものか。
 *
 * 受け口はこれを**必須にしない**（本文が空でも解除は受け付ける）。
 * 判定に使うのは「同意なく解除された」と主張されたときのために
 * `unsubscribe_records.method` を `oneclick_http` と `preference_center` の
 * どちらで記録するかを決めるため。
 */
export function isOneClickPost(body: unknown): boolean {
  if (typeof body === 'string') return body.includes('List-Unsubscribe=One-Click');
  if (body && typeof body === 'object') {
    const value = (body as Record<string, unknown>)['List-Unsubscribe'];
    return value === 'One-Click';
  }
  return false;
}
